import { AudioBus } from '@/Utils/bus';
import { isSafari, isIOS } from '@/Utils/device';
// import localforage from 'localforage';

export default {
  data() {
    return {
      audioElements: [],
      audioAutoplay: true,
      audioPlaying: null,
      audioLastPlayed: null,
      audioWasPlayed: [],
      audioLoading: {},
      audioLoaded: {},
      audioProgress: {},
      audioErrored: {},
      audioEnded: {},
      audioPosition: {},
      audioToPreload: {},
      audioCanPlay: {},
      activeAudioIndex: 0,
      playbackRates: [1, 1.2, 1.4, 1.6, 1.8, 2],
      playbackRate: 1,
    };
  },

  computed: {
    currentTrackIndex() {
      return this.audioPlaying || this.audioLastPlayed;
    },

    hasLoadingErrors() {
      return Object.keys(this.audioErrored).length;
    },
  },

  watch: {
    currentTrackIndex(val, oldVal) {
      if (val != oldVal) {
        this.activeAudioIndex = val;
        AudioBus.$emit('activeAudioIndex', val);
        this.audioRemoteControls();
      }
    },

    audioCanPlay: {
      deep: true,
      handler(val) {
        AudioBus.$emit('audioCanPlay', val);
      },
    },

    audioPlaying(val) {
      AudioBus.$emit('audioPlaying', val);
    },

    playbackRate(val) {
      AudioBus.$emit('playbackRate', val);
    },

    audioProgress: {
      deep: true,
      handler(val) {
        AudioBus.$emit('audioProgress', val);
      },
    },

    audioPosition: {
      deep: true,
      handler(val) {
        AudioBus.$emit('audioPosition', val);
      },
    },

    audioLoading: {
      deep: true,
      handler(val) {
        AudioBus.$emit('audioLoading', val);
      },
    },
  },

  mounted() {
    this.audioElements.forEach((audio, index) => {
      audio.addEventListener('loadstart', this.onAudioEvent.bind(this, index));
      audio.addEventListener('loadeddata', this.onAudioEvent.bind(this, index));
      audio.addEventListener('loadedmetadata', this.onAudioEvent.bind(this, index));
      audio.addEventListener('canplay', this.onAudioEvent.bind(this, index));
      audio.addEventListener('progress', this.onAudioEvent.bind(this, index));
      audio.addEventListener('play', this.onAudioEvent.bind(this, index));
      audio.addEventListener('timeupdate', this.onAudioEvent.bind(this, index));
      audio.addEventListener('pause', this.onAudioEvent.bind(this, index));
      audio.addEventListener('ended', this.onAudioEvent.bind(this, index));
      audio.addEventListener('error', this.onAudioEvent.bind(this, index));
      audio.addEventListener('seeking', this.onAudioEvent.bind(this, index));
      audio.addEventListener('waiting', this.onAudioEvent.bind(this, index));
      audio.addEventListener('stalling', this.onAudioEvent.bind(this, index));
      // audio.addEventListener('canplaythrough', this.onAudioEvent.bind(this, index));
    });

    this.audioRemoteControls();

    // Setup a touch start listener to attempt an unlock in.
    document.addEventListener('touchstart', this.unlock, true);
    document.addEventListener('touchend', this.unlock, true);
    document.addEventListener('click', this.unlock, true);
    document.addEventListener('keydown', this.unlock, true);

    AudioBus.$on('playNow', ({ playTracks }) => {
      if (!playTracks) return;
      if (this.audioPlaying !== null) {
        this.audioPause();
      } else {
        this.audioPlay(this.activeAudioIndex);
      }
    });

    AudioBus.$on('onPlay', (index) => {
      this.audioPlay(index);
    });

    AudioBus.$on('onPause', () => {
      this.audioPause(this.activeAudioIndex);
    });

    AudioBus.$on('onSeekbackward', () => {
      this.audioSeekBack();
    });

    AudioBus.$on('onSeekforward', () => {
      this.audioSeekForward();
    });

    AudioBus.$on('onPrevioustrack', (index) => {
      this.audioPrevious(index);
    });

    AudioBus.$on('onNexttrack', (index) => {
      this.audioNext(index);
    });

    AudioBus.$on('onSeek', ({ event, index }) => {
      this.audioSeek(event, index);
    });

    AudioBus.$on('onPlaybackRate', (args = {}) => {
      this.audioPlaybackRate(args?.playbackRate, args?.increase);
    });
  },

  beforeUnmount() {
    this.audioElements.forEach((audio, index) => {
      audio?.removeEventListener('loadstart', this.onAudioEvent, false);
      audio?.removeEventListener('loadeddata', this.onAudioEvent, false);
      audio?.removeEventListener('loadedmetadata', this.onAudioEvent, false);
      audio?.removeEventListener('canplay', this.onAudioEvent, false);
      audio?.removeEventListener('progress', this.onAudioEvent, false);
      audio?.removeEventListener('play', this.onAudioEvent, false);
      audio?.removeEventListener('timeupdate', this.onAudioEvent, false);
      audio?.removeEventListener('pause', this.onAudioEvent, false);
      audio?.removeEventListener('ended', this.onAudioEvent, false);
      audio?.removeEventListener('error', this.onAudioEvent, false);
      audio?.removeEventListener('seeking', this.onAudioEvent, false);
      audio?.removeEventListener('waiting', this.onAudioEvent, false);
      audio?.removeEventListener('stalling', this.onAudioEvent, false);
      // audio?.removeEventListener('canplaythrough', this.onAudioEvent, false);
    });

    // Remove the touch start listener.
    document.removeEventListener('touchstart', this.unlock, true);
    document.removeEventListener('touchend', this.unlock, true);
    document.removeEventListener('click', this.unlock, true);
    document.removeEventListener('keydown', this.unlock, true);

    AudioBus.$off('playNow');
    AudioBus.$off('onPlay');
    AudioBus.$off('onPause');
    AudioBus.$off('onSeekbackward');
    AudioBus.$off('onSeekforward');
    AudioBus.$off('onPrevioustrack');
    AudioBus.$off('onNexttrack');
    AudioBus.$off('onSeek');
    AudioBus.$off('onPlaybackRate');
  },

  methods: {
    audioRemoteControls() {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', () => {
          AudioBus.$emit('onPlay', this.activeAudioIndex);
        });
        navigator.mediaSession.setActionHandler('pause', () => {
          AudioBus.$emit('onPause');
        });
        navigator.mediaSession.setActionHandler('seekbackward', () => {
          AudioBus.$emit('onSeekbackward');
        });
        navigator.mediaSession.setActionHandler('seekforward', () => {
          AudioBus.$emit('onSeekforward');
        });
        navigator.mediaSession.setActionHandler('previoustrack', () => {
          AudioBus.$emit('onPrevioustrack', this.activeAudioIndex);
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => {
          AudioBus.$emit('onNexttrack', this.activeAudioIndex);
        });
      }
    },

    async unlock() {
      if (isIOS) {
        try {
          var ctx = new AudioContext();
          var source = ctx.createBufferSource();
          source.buffer = ctx.createBuffer(1, 1, 22050);
          source.connect(ctx.destination);

          // Play the empty buffer.
          if (typeof source.start === 'undefined') {
            source.noteOn(0);
          } else {
            source.start(0);
          }
        } catch (e) {
          console.error(e);
        }

        let audio = this.audioElements[this.activeAudioIndex];
        if (audio) {
          await audio.load();
        }
      }

      // Remove the touch start listener.
      document.removeEventListener('touchstart', this.unlock, true);
      document.removeEventListener('touchend', this.unlock, true);
      document.removeEventListener('click', this.unlock, true);
      document.removeEventListener('keydown', this.unlock, true);
    },

    async audioPlay(index = null) {
      if (index === null) index = 0;
      this.audioToPreload[index] = true;
      await this.audioPause();
      this.audioToPreload[index + 1] = true;
      let audio = this.audioElements[index];
      if (audio) {
        audio.playbackRate = this.playbackRate;
        if (this.audioWasPlayed.indexOf(index) === -1) {
          // Set currentTime from trackPlays
          if (this.trackPlaysByIndex?.hasOwnProperty(index)) {
            let trackPlay = this.trackPlaysByIndex[index];
            if (trackPlay.end_seconds) {
              audio.currentTime = trackPlay.end_seconds;
            }
          }
          // Don't do it again
          this.audioWasPlayed.push(index);
        }
        let playPromise;
        try {
          playPromise = audio.play();
        } catch (err) {
          this.audioPause(index);
        }
        return playPromise;
      }
      return new Promise((res) => res);
    },

    async audioPause(index = null) {
      if (index === null) {
        return this.pauseAllAudio();
      }
      let audio = this.audioElements[index];
      if (audio) {
        audio.currentTime = Math.max(audio.currentTime - 3, 0);
        return audio.pause();
      }
      return new Promise((res) => res);
    },

    audioSeek(event, index) {
      let audio = this.audioElements[index];
      let pos = 0;
      if (event.type === 'input') {
        pos = parseFloat(event.target.value) / 100;
      } else {
        let rect = event.target.getBoundingClientRect();
        pos = event.clientX / rect.width;
      }
      if (audio?.duration) {
        let lastTime = audio.currentTime;
        audio.currentTime = parseFloat(audio.duration * pos);
        this.audioLastPlayed = index;
        // Mark `audioWasPlayed` as true so it doesn't try to load last played spot
        // on page load, after seeking and before playing
        this.audioWasPlayed.push(index);
        if (this.audioPlaying !== null) {
          AudioBus.$emit('seekAndPlay', { index, audio, event: 'seekAndPlay', lastTime });
        }
      }
    },

    audioSeekForward(index = null) {
      if (index === null) index = this.activeAudioIndex || this.audioLastPlayed || 0;

      let audio = this.audioElements[index];

      if (audio?.currentTime && audio?.duration)
        audio.currentTime = Math.min(audio.currentTime + 30, audio.duration);
    },

    audioSeekBack(index = null) {
      if (index === null) index = this.activeAudioIndex || this.audioLastPlayed || 0;

      let audio = this.audioElements[index];
      if (audio) audio.currentTime = Math.max(audio.currentTime - 30, 0);
    },

    audioNext(index = null, force = false) {
      if (index === null) index = this.activeAudioIndex || this.audioLastPlayed || 0;

      if (index + 1 < this.audioElements.length) {
        index++;
        this.audioToPreload[index] = true;
        this.activeAudioIndex = index;
        this.audioLastPlayed = index;
        this.audioWasPlayed.push(index);
      } else {
        let audio = this.audioElements[index];
        if (audio) {
          audio.currentTime = 0;
          return this.audioPause();
        }
      }

      // Set new track's time to 0
      let audio = this.audioElements[index];
      if (audio) audio.currentTime = 0;

      if (force === true || this.audioPlaying !== null) {
        return this.audioPlay(index);
      } else {
        return this.audioPause();
      }
    },

    audioPrevious(index = null, force = false) {
      if (index === null) index = this.activeAudioIndex || this.audioLastPlayed || 0;

      if (index - 1 >= 0) {
        index--;
        this.audioToPreload[index] = true;
        this.activeAudioIndex = index;
        this.audioLastPlayed = index;
        this.audioWasPlayed.push(index);
      } else {
        let audio = this.audioElements[index];
        if (audio) audio.currentTime = 0;
        return this.audioPause();
      }

      // Set new track's time to 0
      let audio = this.audioElements[index];
      if (audio) audio.currentTime = 0;

      if (force === true || this.audioPlaying !== null) {
        return this.audioPlay(index);
      } else {
        return this.audioPause();
      }
    },

    audioPlaybackRate(playbackRate = null, increase = true) {
      let index = this.playbackRates.indexOf(playbackRate || this.playbackRate);
      if (increase) {
        if (index + 1 >= this.playbackRates.length) {
          index = 0;
        } else {
          index++;
        }
      }
      let newRate = isNaN(this.playbackRates[index]) ? 1 : parseFloat(this.playbackRates[index]);
      this.playbackRate = newRate;

      let audio = this.audioElements[this.activeAudioIndex];
      if (audio?.playbackRate) {
        audio.playbackRate = newRate;
      }
    },

    pauseAllAudio() {
      let promises = [];
      for (var i = this.audioElements.length - 1; i >= 0; i--) {
        let audio = this.audioElements[i];
        if (audio) {
          let promise = audio.pause();
          promises.push(promise);
        }
      }
      return Promise.all(promises);
    },

    onTrackClick(index) {
      if (this.isTrackPlaying(index)) {
        return this.audioPause(index);
      } else {
        return this.audioPlay(index);
      }
    },

    isTrackActive(index) {
      return this.activeAudioIndex === index;
    },

    isTrackPlaying(index) {
      return this.audioPlaying === index;
    },

    isTrackPaused(index) {
      return this.audioPlaying === null && this.isTrackActive(index);
    },

    isTrackLoading(index) {
      return this.audioLoading[index];
    },

    isTrackLoaded(index) {
      return this.audioLoaded[index];
    },

    isTrackErrored(index) {
      return this.audioErrored[index];
    },

    isTrackEnded(index) {
      return this.audioEnded[index];
    },

    isTrackReady(index) {
      return this.audioCanPlay[index];
    },

    getTrackProgressWidth(index) {
      return this.audioProgress[index] + '%';
    },

    getTrackPositionWidth(index) {
      return this.audioPosition[index] + '%';
    },

    getTrackError(index) {
      return this.audioErrored[index];
    },

    onAudioEvent(index, ev) {
      // if (index == this.activeAudioIndex) console.log('%c ev -->', 'color:#F80', ev);
      let audio = ev.target;
      switch (ev.type) {
        case 'loadedmetadata':
          AudioBus.$emit('loadedmetadata', { index, audio, event: 'loadedmetadata' });
          break;
        case 'waiting':
          this.audioLoading[index] = true;
          break;
        case 'loadstart':
          this.audioLoading[index] = true;
          setTimeout(() => {
            if (this.audioLoading[index] === true) this.audioLoading[index] = false;
          }, 500);
          break;
        case 'loadeddata':
          this.audioLoading[index] = false;
          this.audioLoaded[index] = true;
          AudioBus.$emit('loadeddata', { index, audio, event: 'loadeddata' });
          setTimeout(() => {
            if (isSafari && this.audioLoading[index] === true) this.audioLoading[index] = false;
          }, 500);
          break;
        case 'suspend':
        case 'seeking':
        case 'stalling':
        case 'loadeddata':
          if (!this.audioCanPlay[index]) this.audioLoading[index] = true;
          setTimeout(() => {
            if (isSafari && this.audioLoading[index] === true) this.audioLoading[index] = false;
          }, 500);
          break;
        case 'canplay':
          this.audioCanPlay[index] = true;
          this.audioLoading[index] = false;
          this.audioLoaded[index] = true;
          break;
        case 'play':
          this.audioCanPlay[index] = true;
          this.audioLoading[index] = false;
          this.audioLoaded[index] = true;
          this.audioPlaying = index;
          this.audioLastPlayed = index;
          AudioBus.$emit('play', { index, audio, event: 'play' });
          break;
        case 'pause':
          this.audioPlaying = null;
          AudioBus.$emit('pause', { index, audio, event: 'pause' });
          break;
        case 'progress':
          if (this.audioCanPlay[index]) {
            this.audioLoading[index] = false;
            this.audioLoaded[index] = true;
          }
          var buffered = audio.buffered;
          var loaded = 0;
          if (buffered.length) {
            loaded = (100 * buffered.end(0)) / audio.duration;
          }
          this.audioProgress[index] = loaded;
          break;
        case 'timeupdate':
          if (this.audioCanPlay[index]) {
            this.audioLoading[index] = false;
            this.audioLoaded[index] = true;
          }
          let played = 0;
          if (audio?.duration) {
            played = (audio.currentTime / audio.duration) * 100;
          }
          this.audioPosition[index] = played;
          AudioBus.$emit('timeupdate', { index, audio, event: 'timeupdate' });
          break;
        case 'ended':
          this.audioEnded[index] = true;
          AudioBus.$emit('ended', { index, audio, event: 'ended' });

          if (this.audioAutoplay) {
            this.audioNext(index, true);
          } else {
            this.audioPlaying = null;
          }
          break;
        case 'stalled':
        case 'error':
          let msg = audio.error.message || 'An error has occurred';
          switch (audio.error.code) {
            case 1:
              msg = 'Loading was aborted';
              break;
            case 2:
              msg = 'Network error; Check connection';
              break;
            case 3:
              msg = 'Problem decoding audio';
              break;
            case 4:
              msg = 'Problem loading audio';
              break;
          }
          this.audioErrored[index] = msg;
          break;
      }
    },

    toggleAudioAutoplay(ev, force = null) {
      this.audioAutoplay = force !== null ? force : !this.audioAutoplay;
    },
  },
};
